/**
 * Logger configuration and initialization
 */
import { pino } from 'pino'
import type { Logger as PinoLogger, LoggerOptions } from 'pino'

export type LogLevel = 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace' | 'silent'

export interface LoggerConfig {
  level?: LogLevel
  prettyPrint?: boolean
  name?: string
  enabled?: boolean
}

/**
 * Checks if code is running in browser environment
 * Uses globalThis to avoid TypeScript DOM type requirements
 */
function isBrowser(): boolean {
  return typeof globalThis !== 'undefined' &&
         'window' in globalThis &&
         'document' in globalThis
}

/**
 * Safely gets environment variable from Node.js or browser context
 */
function getEnvVar(key: string, defaultValue: string = ''): string {
  if (isBrowser()) {
    // In browser, check import.meta.env (Vite) or return default
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return (import.meta.env[key] as string) || defaultValue
    }
    return defaultValue
  }
  // In Node.js, use process.env
  return process.env[key] || defaultValue
}

/**
 * Creates a configured logger instance
 */
export function createLogger(config: LoggerConfig = {}): PinoLogger {
  const {
    level = (getEnvVar('LOG_LEVEL') as LogLevel) || 'info',
    prettyPrint = getEnvVar('NODE_ENV') !== 'production',
    name,
    enabled = true
  } = config

  const options: LoggerOptions = {
    level,
    enabled,
    ...(name && { name }),
  }

  // In browser, use browser console
  if (isBrowser()) {
    return pino({
      ...options,
      browser: {
        asObject: false,
      }
    })
  }

  // Use pretty printing in development
  if (prettyPrint) {
    return pino({
      ...options,
      transport: {
        target: 'pino-pretty',
        options: {
          colorize: true,
          translateTime: 'SYS:standard',
          ignore: 'pid,hostname',
        }
      }
    })
  }

  // Use standard JSON logging in production
  return pino(options)
}

/**
 * Default logger instance
 */
export const logger = createLogger()

/**
 * Child logger factory for creating scoped loggers
 */
export function createChildLogger(bindings: Record<string, any>): PinoLogger {
  return logger.child(bindings)
}

// Re-export types from pino
export type { Logger, LoggerOptions } from 'pino'